import React, { useState, useMemo } from 'react';
import {
  ChevronRightIcon,
  ChevronDownIcon,
  ServerIcon,
  TableCellsIcon,
  EyeIcon,
  CubeIcon,
  FolderIcon,
  FolderOpenIcon
} from '@heroicons/react/24/outline';

const HierarchicalDataExplorer = ({ 
  data = [], 
  onItemSelect, 
  selectedItem = null,
  renderItemActions = null,
  renderItemBadges = null,
  title = "Data Objects",
  className = "",
  showCounts = true,
  collapsedByDefault = false
}) => {
  const [expandedItems, setExpandedItems] = useState(new Set());

  // Organize data hierarchically: database -> schema -> objects
  const hierarchyData = useMemo(() => {
    const hierarchy = {};

    data.forEach((item) => {
      const database = item.database_name || 'Unknown Database';
      const schema = item.schema_name || 'Unknown Schema';
      
      if (!hierarchy[database]) {
        hierarchy[database] = {};
      }
      
      if (!hierarchy[database][schema]) {
        hierarchy[database][schema] = [];
      }
      
      hierarchy[database][schema].push(item);
    });

    return hierarchy;
  }, [data]);

  // Calculate counts for each level
  const getCounts = useMemo(() => {
    const counts = {};
    
    Object.entries(hierarchyData).forEach(([database, schemas]) => {
      let dbTotalObjects = 0;
      let dbTotalSchemas = Object.keys(schemas).length;
      
      Object.entries(schemas).forEach(([schema, objects]) => {
        const schemaObjectCount = objects.length;
        dbTotalObjects += schemaObjectCount;
        
        counts[`${database}.${schema}`] = schemaObjectCount;
      });
      
      counts[database] = { schemas: dbTotalSchemas, objects: dbTotalObjects };
    });
    
    return counts;
  }, [hierarchyData]);

  const toggleExpanded = (key) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedItems(newExpanded);
  };

  // Normalize object type to handle enum values like "TableType.TABLE"
  const normalizeObjectType = (objectType) => {
    if (!objectType) return 'table';

    // Handle string enum values like "TableType.TABLE" or "ObjectType.VIEW"
    if (typeof objectType === 'string') {
      if (objectType.includes('.')) {
        // Extract the part after the dot and lowercase it
        const parts = objectType.split('.');
        return parts[parts.length - 1].toLowerCase();
      }
      return objectType.toLowerCase();
    }

    return 'table';
  };

  const getObjectIcon = (objectType) => {
    const normalized = normalizeObjectType(objectType);
    switch (normalized) {
      case 'table':
        return TableCellsIcon;
      case 'view':
        return EyeIcon;
      case 'materialized view':
      case 'materialized_view':
        return CubeIcon;
      default:
        return TableCellsIcon;
    }
  };

  const getObjectTypeColor = (objectType) => {
    const normalized = normalizeObjectType(objectType);
    switch (normalized) {
      case 'table':
        return 'text-blue-600 bg-blue-50';
      case 'view':
        return 'text-green-600 bg-green-50';
      case 'materialized view':
      case 'materialized_view':
        return 'text-purple-600 bg-purple-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const isSelected = (item) => {
    if (!selectedItem) return false;
    return selectedItem.full_name === item.full_name || 
           selectedItem.object_name === item.object_name ||
           selectedItem === item.full_name;
  };

  if (!data || data.length === 0) {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
        <div className="text-center py-8">
          <ServerIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No data objects found</h3>
          <p className="text-gray-500">
            Connect to a data source and run discovery to see your database structure.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg border border-gray-200 ${className}`}>
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <ServerIcon className="h-5 w-5 text-gray-500" />
            <h3 className="text-lg font-medium text-gray-900">{title}</h3>
          </div>
          {showCounts && (
            <div className="text-sm text-gray-500">
              {Object.keys(hierarchyData).length} database{Object.keys(hierarchyData).length !== 1 ? 's' : ''} • {data.length} object{data.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      </div>

      {/* Hierarchical Tree */}
      <div className="divide-y divide-gray-100">
        {Object.entries(hierarchyData).map(([database, schemas]) => {
          const databaseKey = database;
          const isDatabaseExpanded = expandedItems.has(databaseKey);
          
          return (
            <div key={database} className="p-4">
              {/* Database Level */}
              <div 
                className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors duration-150"
                onClick={() => toggleExpanded(databaseKey)}
              >
                {isDatabaseExpanded ? (
                  <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                )}
                {isDatabaseExpanded ? (
                  <FolderOpenIcon className="h-5 w-5 text-blue-600" />
                ) : (
                  <FolderIcon className="h-5 w-5 text-blue-600" />
                )}
                <span className="font-medium text-gray-900">{database}</span>
                {showCounts && (
                  <span className="text-xs text-gray-500 font-medium">
                    {getCounts[database].schemas} schema{getCounts[database].schemas !== 1 ? 's' : ''} • {getCounts[database].objects} object{getCounts[database].objects !== 1 ? 's' : ''}
                  </span>
                )}
              </div>

              {/* Schema Level */}
              {isDatabaseExpanded && (
                <div className="ml-6 mt-2 space-y-2">
                  {Object.entries(schemas).map(([schema, objects]) => {
                    const schemaKey = `${database}.${schema}`;
                    const isSchemaExpanded = expandedItems.has(schemaKey);
                    
                    return (
                      <div key={schemaKey}>
                        {/* Schema Header */}
                        <div 
                          className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors duration-150"
                          onClick={() => toggleExpanded(schemaKey)}
                        >
                          {isSchemaExpanded ? (
                            <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                          ) : (
                            <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                          )}
                          {isSchemaExpanded ? (
                            <FolderOpenIcon className="h-4 w-4 text-green-600" />
                          ) : (
                            <FolderIcon className="h-4 w-4 text-green-600" />
                          )}
                          <span className="font-medium text-gray-800">{schema}</span>
                          {showCounts && (
                            <span className="text-xs text-gray-500 font-medium">
                              {getCounts[schemaKey]} object{getCounts[schemaKey] !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>

                        {/* Objects Level */}
                        {isSchemaExpanded && (
                          <div className="ml-6 mt-1 space-y-1">
                            {objects.map((object, index) => {
                              const ObjectIcon = getObjectIcon(object.object_type);
                              const selected = isSelected(object);
                              
                              return (
                                <div
                                  key={`${database}.${schema}.${object.object_name || object.name || `object-${index}`}`}
                                  className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors duration-150 group ${
                                    selected 
                                      ? 'bg-blue-50 border border-blue-200' 
                                      : 'hover:bg-gray-50 border border-transparent'
                                  }`}
                                  onClick={() => onItemSelect && onItemSelect(object)}
                                >
                                  <div className="flex items-center space-x-3 min-w-0 flex-1">
                                    <ObjectIcon className={`h-4 w-4 flex-shrink-0 ${
                                      selected ? 'text-blue-600' : 'text-gray-500'
                                    }`} />
                                    <div className="min-w-0 flex-1 py-1">
                                      <div className={`font-medium text-sm ${
                                        selected ? 'text-blue-900' : 'text-gray-900'
                                      }`}>
                                        {typeof object.object_name === 'string' ? object.object_name : object.name || 'Unknown Object'}
                                      </div>
                                    </div>
                                  </div>

                                  <div className="flex items-center space-x-2 flex-shrink-0">
                                    {/* Custom Badges */}
                                    {renderItemBadges && renderItemBadges(object)}

                                    {/* Custom Actions */}
                                    {renderItemActions && (
                                      <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                                        {renderItemActions(object)}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HierarchicalDataExplorer;